/**
 * BuildingEditorPanel - Right-side panel for hangar editor
 *
 * Renders as a 420px absolute overlay that slides in from the right edge. It sits
 * on top of the map rather than resizing it, so the Mapbox + Three.js scene never
 * flickers when the panel opens/closes. Stays mounted; parks off-screen via a
 * translate transform in view mode.
 * Follows industry standard (Figma, SketchUp, Blender) of properties on right.
 *
 * Displays context-sensitive content:
 * - Template selector when starting to add hangar
 * - Building form when editing (routes to correct form based on template)
 */

import { useBuildingEditorStore, type SingleUnitFormValues, type OutdoorRectangularFormValues } from '@/stores/buildingEditorStore'
import { useTenant } from '@/contexts'
import { TemplateSelector } from './TemplateSelector'
import { OutdoorTemplateSelector } from './OutdoorTemplateSelector'
import { BuildingForm } from './BuildingForm'
import { SingleRowForm } from './forms/SingleRowForm'
import { DoubleRowForm } from './forms/DoubleRowForm'
import { TRowForm } from './forms/TRowForm'
import { NestedTRowForm } from './forms/NestedTRowForm'
import { ArchHangarForm } from './forms/ArchHangarForm'
import { OutdoorAreaForm } from './forms/OutdoorAreaForm'
import { useSaveArea, useSaveMultiUnitArea, useSaveOutdoorArea } from '@/hooks/useCreateArea'
import type { PricingFormFields } from '@/lib/schemas/pricing-fields'
import type { AreaPlacementFieldsInput } from '@/types/area-placement'
import { generateAreaCode } from '@/lib/utils/areaCodeGenerator'
import { Building2, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { TemplateType, SingleRowParams, DoubleRowParams, THangarParams, NestedTHangarParams, ArchHangarParams } from '@/lib/templates'
import { toast } from 'sonner'
import { MapPin, RotateCw } from 'lucide-react'

// ============================================================================
// PLACEMENT HINT COMPONENT
// Shows guidance when building needs to be placed on the map
// ============================================================================
function PlacementHint() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mx-4 mt-4">
      <div className="flex items-start gap-2">
        <MapPin className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-blue-800 font-medium">
            Click on the map to place hangar
          </p>
          <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
            <RotateCw className="h-3 w-3" />
            <span>Hold Shift + Scroll to rotate</span>
          </div>
        </div>
      </div>
    </div>
  )
}

interface BuildingEditorPanelProps {
  locationId?: string
}

export function BuildingEditorPanel({ locationId }: BuildingEditorPanelProps) {
  const { currentLocation, currentOrganization } = useTenant()

  // Use destructured store access (proven to work in original)
  const {
    mode,
    templateType,
    previewBuilding,
        editingBuildingId,
    editingUnitId,
    isLoadingBuilding,
    isPlaced,
    formValues,
    singleRowFormValues,
    doubleRowFormValues,
    tHangarFormValues,
    nestedTHangarFormValues,
    archHangarFormValues,
    outdoorFormValues,
    pricingValues,
    placementValues,
    isDuplicating,
    pendingOutdoorSubtype,
    mapCenter,
    cancelAddBuilding,
    cancelEdit,
    startAddBuilding,
    startEditBuilding,
    reset,
  } = useBuildingEditorStore()

  const saveArea = useSaveArea()
  const saveMultiUnitArea = useSaveMultiUnitArea()
  const saveOutdoorArea = useSaveOutdoorArea()

  // Default Detroit coordinates (fallback if mapCenter not set yet)
  const DEFAULT_CENTER = { lat: 42.410097, lng: -83.005169 }

  // Handle template selection - create preview building at map center
  const handleTemplateSelect = async (template: TemplateType) => {
    // Use mapCenter if available, otherwise fall back to Detroit default
    const center = mapCenter || DEFAULT_CENTER

    // Generate a unique area code based on building type
    let generatedCode: string | undefined
    if (currentOrganization?.id) {
      try {
        // Map template type to building type for code generation
        // For outdoor templates, use the pending subtype; otherwise use hangar type
        const buildingType = template === 'outdoor_rectangular'
          ? (pendingOutdoorSubtype || 'ramp_area')
          : 'single_unit_hangar'
        generatedCode = await generateAreaCode(currentOrganization.id, buildingType)
      } catch (error) {
        console.error('Failed to generate area code:', error)
        // Fall through to use default code
      }
    }

    startAddBuilding(template, center.lat, center.lng, generatedCode)
  }

  // Handle save for single-unit template
  const handleSingleUnitSave = async (values: SingleUnitFormValues, pricing?: PricingFormFields) => {
    const finalLocationId = locationId || currentLocation?.id

    if (!finalLocationId) {
      toast.error('No location selected')
      return
    }

    if (!previewBuilding) {
      toast.error('Building position not set')
      return
    }

    try {
      const savedArea = await saveArea.mutateAsync({
        locationId: finalLocationId,
        formValues: values,
        latitude: previewBuilding.latitude,
        longitude: previewBuilding.longitude,
        areaId: editingBuildingId,
        unitId: editingUnitId,
        pricing,
      })

      // Keep the panel open after save (user closes it manually via X). When
      // creating, transition into edit mode of the new area so re-saving updates
      // instead of creating a duplicate.
      if (!editingBuildingId && savedArea?.id) {
        startEditBuilding(savedArea.id)
      }
    } catch (error) {
      // Error is already shown via toast from the mutation hook's onError
      console.error('Failed to save building:', error instanceof Error ? error.message : error)
    }
  }

  // Handle save for multi-unit templates (single-row, double-row, t-hangar)
  const handleMultiUnitSave = async (values: SingleRowParams | DoubleRowParams | THangarParams | NestedTHangarParams | ArchHangarParams, pricing?: PricingFormFields) => {
    const finalLocationId = locationId || currentLocation?.id

    if (!finalLocationId) {
      toast.error('No location selected')
      return
    }

    if (!previewBuilding) {
      toast.error('Building position not set')
      return
    }

    try {
      // Update values with current position
      const updatedValues = {
        ...values,
        latitude: previewBuilding.latitude,
        longitude: previewBuilding.longitude,
        bearing_deg: previewBuilding.bearing_deg,
      }

      const savedArea = await saveMultiUnitArea.mutateAsync({
        locationId: finalLocationId,
        templateType: templateType as 'single_row' | 'double_row' | 'standard_t_hangar' | 'nested_t_hangar' | 'arch_hangar',
        params: updatedValues,
        areaId: editingBuildingId,
        pricing,
      })

      // Keep the panel open after save; transition create→edit on the new area.
      if (!editingBuildingId && savedArea?.id) {
        startEditBuilding(savedArea.id)
      }
    } catch (error) {
      // Error is already shown via toast from the mutation hook's onError
      console.error('Failed to save multi-unit building:', error instanceof Error ? error.message : error)
    }
  }

  // Handle save for outdoor area template
  const handleOutdoorSave = async (
    values: OutdoorRectangularFormValues,
    pricing?: PricingFormFields,
    placement?: AreaPlacementFieldsInput
  ) => {
    const finalLocationId = locationId || currentLocation?.id

    if (!finalLocationId) {
      toast.error('No location selected')
      return
    }

    if (!previewBuilding) {
      toast.error('Area position not set')
      return
    }

    try {
      const savedArea = await saveOutdoorArea.mutateAsync({
        locationId: finalLocationId,
        formValues: values,
        latitude: previewBuilding.latitude,
        longitude: previewBuilding.longitude,
        areaId: editingBuildingId,
        unitId: editingUnitId,
        pricing,
        placement,
      })

      // Keep the panel open after save; transition create→edit on the new area.
      if (!editingBuildingId && savedArea?.id) {
        startEditBuilding(savedArea.id)
      }
    } catch (error) {
      console.error('Failed to save outdoor area:', error instanceof Error ? error.message : error)
    }
  }

  // Handle cancel
  const handleCancel = () => {
    if (mode === 'add') {
      cancelAddBuilding()
    } else if (mode === 'edit') {
      cancelEdit()
    }
  }

  // Panel is an overlay that slides in from the right (instead of a flex sibling
  // that resizes the map and causes flicker). Kept mounted so it can animate both
  // in and out; visibility is driven by the transform below. In view mode all the
  // content branches render nothing, so the parked panel is effectively empty.
  const open = mode !== 'view'

  return (
    <div
      className={`absolute right-0 top-0 z-30 w-[420px] border-l bg-background flex flex-col h-full overflow-hidden shadow-lg transition-transform duration-300 ease-out ${
        open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">
            {mode === 'add'
              ? (isDuplicating
                ? 'Duplicate Building'
                : templateType === 'outdoor_rectangular'
                  ? (outdoorFormValues?.area_subtype === 'tiedown_area' ? 'New Tiedown Area' : 'New Ramp Area')
                  : pendingOutdoorSubtype
                    ? (pendingOutdoorSubtype === 'tiedown_area' ? 'New Tiedown Area' : 'New Ramp Area')
                    : 'New Hangar')
              : templateType === 'outdoor_rectangular'
                ? 'Edit Outdoor Area'
                : 'Edit Hangar'}
          </h2>
        </div>
        <Button variant="ghost" size="icon" onClick={handleCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      {/* Show outdoor template selector when adding outdoor area */}
      {mode === 'add' && !templateType && pendingOutdoorSubtype && (
        <ScrollArea className="flex-1">
          <div className="p-4">
            <OutdoorTemplateSelector onSelect={handleTemplateSelect} />
          </div>
        </ScrollArea>
      )}

      {/* Show hangar template selector if no template selected and not outdoor */}
      {mode === 'add' && !templateType && !pendingOutdoorSubtype && (
        <ScrollArea className="flex-1">
          <div className="p-4">
            <TemplateSelector onSelect={handleTemplateSelect} />
          </div>
        </ScrollArea>
      )}

      {/* Show form when template is selected - routes to correct form based on template */}
      {mode === 'add' && templateType === 'single_unit_community' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <BuildingForm
            onSave={handleSingleUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveArea.isPending}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'single_row' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <SingleRowForm
            onSave={handleMultiUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveMultiUnitArea.isPending}
            previewLatitude={previewBuilding?.latitude}
            previewLongitude={previewBuilding?.longitude}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'double_row' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <DoubleRowForm
            onSave={handleMultiUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveMultiUnitArea.isPending}
            previewLatitude={previewBuilding?.latitude}
            previewLongitude={previewBuilding?.longitude}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'standard_t_hangar' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <TRowForm
            onSave={handleMultiUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveMultiUnitArea.isPending}
            previewLatitude={previewBuilding?.latitude}
            previewLongitude={previewBuilding?.longitude}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'nested_t_hangar' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <NestedTRowForm
            onSave={handleMultiUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveMultiUnitArea.isPending}
            previewLatitude={previewBuilding?.latitude}
            previewLongitude={previewBuilding?.longitude}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'arch_hangar' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <ArchHangarForm
            onSave={handleMultiUnitSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveMultiUnitArea.isPending}
            previewLatitude={previewBuilding?.latitude}
            previewLongitude={previewBuilding?.longitude}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {mode === 'add' && templateType === 'outdoor_rectangular' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {!isPlaced && <PlacementHint />}
          <OutdoorAreaForm
            onSave={handleOutdoorSave}
            onCancel={handleCancel}
            isCreating={true}
            isSaving={saveOutdoorArea.isPending}
            isPlaced={isPlaced}
          />
        </div>
      )}

      {/* EDIT MODE — Building form only (obstacles/subunits use view-mode side panels) */}
      {mode === 'edit' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {isLoadingBuilding && (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Loading building...</span>
              </div>
            </div>
          )}

          {!isLoadingBuilding && templateType === 'single_row' && singleRowFormValues && (
            <SingleRowForm
              onSave={handleMultiUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveMultiUnitArea.isPending}
              previewLatitude={previewBuilding?.latitude}
              previewLongitude={previewBuilding?.longitude}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'double_row' && doubleRowFormValues && (
            <DoubleRowForm
              onSave={handleMultiUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveMultiUnitArea.isPending}
              previewLatitude={previewBuilding?.latitude}
              previewLongitude={previewBuilding?.longitude}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'standard_t_hangar' && tHangarFormValues && (
            <TRowForm
              onSave={handleMultiUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveMultiUnitArea.isPending}
              previewLatitude={previewBuilding?.latitude}
              previewLongitude={previewBuilding?.longitude}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'nested_t_hangar' && nestedTHangarFormValues && (
            <NestedTRowForm
              onSave={handleMultiUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveMultiUnitArea.isPending}
              previewLatitude={previewBuilding?.latitude}
              previewLongitude={previewBuilding?.longitude}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'arch_hangar' && archHangarFormValues && (
            <ArchHangarForm
              onSave={handleMultiUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveMultiUnitArea.isPending}
              previewLatitude={previewBuilding?.latitude}
              previewLongitude={previewBuilding?.longitude}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'outdoor_rectangular' && outdoorFormValues && (
            <OutdoorAreaForm
              onSave={handleOutdoorSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveOutdoorArea.isPending}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
              initialPlacement={placementValues ?? undefined}
            />
          )}

          {!isLoadingBuilding && templateType === 'single_unit_community' && formValues && (
            <BuildingForm
              onSave={handleSingleUnitSave}
              onCancel={handleCancel}
              isCreating={false}
              isSaving={saveArea.isPending}
              areaId={editingBuildingId!}
              areaName={previewBuilding?.name}
              onRemoved={reset}
              initialPricing={pricingValues ?? undefined}
            />
          )}
        </div>
      )}
    </div>
  )
}
